import { useMutation } from "@apollo/client";
import ApprovalOutlined from "@mui/icons-material/ApprovalOutlined";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  VerifyAndSubmitFormDialog,
  VerifyAndSubmitFormState,
} from "components/VerifyAndSubmitFormDialog";
import { hasValue } from "encamp-shared/src/utils/hasValue";
import { TierIiReportEncampStatus } from "generated-graphql/graphql";
import { BULK_VERIFY_MUTATION } from "queries/tierIIVerification";
import { useCallback, useMemo } from "react";

import { useBulkActionContext } from ".";

export function VerifyAndSubmitMenuItem() {
  const { setDialogState } = useBulkActionContext();

  return (
    <MenuItem onClick={() => setDialogState("verify&submit")}>
      <ListItemIcon>
        <ApprovalOutlined />
      </ListItemIcon>
      <ListItemText>Verify & Submit</ListItemText>
    </MenuItem>
  );
}

export function VerifyAndSubmitDialog() {
  const alerts = useAlerts();
  const { dialogState, onClose, selectedRows } = useBulkActionContext();

  const verifiableRows = useMemo(
    () =>
      selectedRows.filter(
        (row) => row.encampStatus === TierIiReportEncampStatus.AwaitingVerification
      ),
    [selectedRows]
  );

  const skippedDueToVerificationCount = selectedRows.length - verifiableRows.length;

  const reportsWithValidationErrorsCount = useMemo(
    () => selectedRows.filter((row) => row.issues > 0).length,
    [selectedRows]
  );
  const verifiableRowsWithoutOpenReviews = useMemo(
    () => verifiableRows.filter((row) => !row.hasIncompleteReviews),
    [verifiableRows]
  );
  const verifiableRowsWithoutOpenReviewsCount = verifiableRowsWithoutOpenReviews.length;

  const skippedDueToOpenReviewsCount =
    verifiableRows.length - verifiableRowsWithoutOpenReviews.length;

  const [mutate, { loading }] = useMutation(BULK_VERIFY_MUTATION, {
    refetchQueries: ["TierIIReportActivities"],
  });

  const submit = useCallback(
    async (formState: VerifyAndSubmitFormState, overrideOpenReviews: boolean) => {
      const reportIds = (overrideOpenReviews ? verifiableRows : verifiableRowsWithoutOpenReviews)
        .map((row) => row.tierIIReportId)
        .filter(hasValue);

      try {
        await mutate({
          variables: {
            reportIds,
            verifier: {
              verifiedAt: formState.date,
              verifierFullName: formState.verifierFullName,
              verifierTitle: formState.verifierTitle,
              verifierEmail: formState.verifierEmail,
              verifierPhone: formState.verifierPhone,
            },
          },
        });
        alerts.info("Starting bulk verification.");
        onClose();
      } catch (err) {
        alerts.error("An error occurred during bulk verification", err);
      }
    },
    [alerts, mutate, onClose, verifiableRows, verifiableRowsWithoutOpenReviews]
  );

  const reportIds = verifiableRows.map((row) => row.tierIIReportId).filter(hasValue);

  return (
    <VerifyAndSubmitFormDialog
      withConfirmation
      open={dialogState === "verify&submit"}
      onClose={onClose}
      loading={loading}
      onSubmit={submit}
      skippedDueToOpenReviewsCount={skippedDueToOpenReviewsCount}
      skippedDueToVerificationCount={skippedDueToVerificationCount}
      verifiableRowsWithoutOpenReviewsCount={verifiableRowsWithoutOpenReviewsCount}
      reportsWithValidationErrorsCount={reportsWithValidationErrorsCount}
      isReportKindAnnual
      reportIds={reportIds}
    />
  );
}
